import { useState } from 'react';
import { Loader2, ArrowLeft, ShieldCheck } from 'lucide-react';

const inputClass =
  'w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-base text-gray-900 placeholder-gray-400 focus:outline-none focus:border-wine focus:ring-2 focus:ring-wine/15 transition-colors';
const SIZES = ['S', 'M', 'L', 'XL', '2XL', '3XL'];

export const ReserveDetailsForm = ({ tier, holdHours, onBack, onSubmit, submitting, error }) => {
  const [f, setF] = useState({ first_name: '', last_name: '', email: '', phone: '', tshirt_size: '', roommate_preference: '', notes: '' });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const valid = f.first_name.trim() && f.last_name.trim() && /@/.test(f.email) && f.phone.trim();

  return (
    <div className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden" data-testid="reserve-details-form">
      <div className="bg-[#1a0d10] text-white px-6 md:px-10 py-6 flex items-center justify-between gap-4">
        <div>
          <p className="eyebrow text-[10px] text-[#d9a15b] tracking-[0.3em] mb-1">Step 2 of 3 · Your details</p>
          <h3 className="font-display text-2xl md:text-3xl font-semibold">{tier.name} · ${tier.price}</h3>
        </div>
        <button type="button" onClick={onBack} className="text-xs text-white/60 hover:text-white inline-flex items-center gap-1" data-testid="reserve-back-btn">
          <ArrowLeft className="w-3.5 h-3.5" /> Change tier
        </button>
      </div>
      <form className="p-6 md:p-10 space-y-6" onSubmit={(e) => { e.preventDefault(); if (valid) onSubmit(f); }} noValidate>
        <div className="grid sm:grid-cols-2 gap-5">
          <Field label="First name" required><input className={inputClass} value={f.first_name} onChange={set('first_name')} data-testid="reserve-first-name" autoComplete="given-name" /></Field>
          <Field label="Last name" required><input className={inputClass} value={f.last_name} onChange={set('last_name')} data-testid="reserve-last-name" autoComplete="family-name" /></Field>
          <Field label="Email" required hint="Your hold confirmation and Venmo instructions go here."><input type="email" className={inputClass} value={f.email} onChange={set('email')} data-testid="reserve-email" autoComplete="email" /></Field>
          <Field label="Mobile phone" required><input type="tel" className={inputClass} value={f.phone} onChange={set('phone')} data-testid="reserve-phone" autoComplete="tel" /></Field>
          <Field label="T-shirt size">
            <select className={inputClass} value={f.tshirt_size} onChange={set('tshirt_size')} data-testid="reserve-tshirt">
              <option value="">Select…</option>
              {SIZES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </Field>
          <Field label="Roommate preference" hint="Anyone you'd like to room with?"><input className={inputClass} value={f.roommate_preference} onChange={set('roommate_preference')} data-testid="reserve-roommate" /></Field>
        </div>
        <Field label="Anything else we should know?"><textarea rows={3} className={inputClass} value={f.notes} onChange={set('notes')} data-testid="reserve-notes" /></Field>

        <div className="flex items-start gap-3 rounded-xl bg-cream-100 border border-cream-200 px-4 py-3 text-sm text-gray-700">
          <ShieldCheck className="w-5 h-5 text-wine shrink-0 mt-0.5" />
          <p>Your bed will be held for <strong>{holdHours} hours</strong> while your Venmo payment is verified. Nothing is charged automatically — you pay in the Venmo app on the next step.</p>
        </div>

        {error && <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-800" data-testid="reserve-error">{error}</div>}

        <button type="submit" disabled={!valid || submitting} data-testid="reserve-submit-btn"
          className="w-full inline-flex items-center justify-center gap-2 bg-gradient-to-r from-wine to-burgundy text-white font-semibold text-base py-4 rounded-full hover:opacity-90 disabled:opacity-50 transition-opacity shadow-lg">
          {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : null}
          {submitting ? 'Holding your bed…' : `Hold my bed · Continue to Venmo`}
        </button>
      </form>
    </div>
  );
};

const Field = ({ label, required, hint, children }) => (
  <label className="block">
    <span className="block text-sm font-semibold text-gray-900 mb-1.5">{label}{required && <span className="text-wine ml-1">*</span>}</span>
    {hint && <span className="block text-xs text-gray-500 mb-2">{hint}</span>}
    {children}
  </label>
);
